import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { MapPin, Phone, TrendingDown, Building2, Shield, Star, Award, DollarSign } from 'lucide-react';

const getGradeColor = (grade) => {
  const colors = {
    'A': 'bg-green-500',
    'B': 'bg-blue-500', 
    'C': 'bg-yellow-500',
    'D': 'bg-orange-500',
    'F': 'bg-red-500'
  };
  return colors[grade] || 'bg-gray-400';
};

const renderStars = (rating) => {
  if (!rating) return null;
  return (
    <div className="flex gap-0.5">
      {[...Array(5)].map((_, i) => (
        <Star 
          key={i} 
          className={`w-4 h-4 ${i < rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`} 
        />
      ))}
    </div>
  );
};

export default function ComparisonView({ comparisons, currentEstimate, insuranceCarrier }) {
  if (!comparisons || comparisons.length === 0) {
    return (
      <Card className="border-0 shadow-xl">
        <CardContent className="py-8 text-center text-gray-500">
          No providers found within 50 miles.
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Insurance-Specific Callout */}
      <Card className="border-0 shadow-lg bg-gradient-to-r from-green-50 to-emerald-50 border-l-4 border-green-500">
        <CardContent className="py-4">
          <div className="flex items-start gap-3">
            <Shield className="w-6 h-6 text-green-600 mt-0.5" />
            <div>
              <h4 className="font-semibold text-green-800 mb-1">Personalized to Your Insurance</h4>
              <p className="text-sm text-green-700">
                All costs below are calculated specifically for your <span className="font-semibold">{insuranceCarrier || 'insurance plan'}</span>, 
                including your deductible, coinsurance, and out-of-pocket maximum. Different insurance carriers have different 
                negotiated rates with each provider - these are YOUR actual costs.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="border-0 shadow-xl" data-testid="provider-comparison">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-2xl">
            <Building2 className="w-6 h-6" />
            Provider Comparison - Your {insuranceCarrier || 'Insurance'} Costs
          </CardTitle>
          <CardDescription>
            Found {comparisons.length} providers within 50 miles • Sorted by lowest out-of-pocket cost for you
          </CardDescription>
        </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {comparisons.map((comp, index) => (
            <div
              key={comp.provider.id}
              data-testid={`provider-${index}`}
              className="bg-white rounded-xl p-5 border-2 border-gray-100 hover:border-blue-200 transition-all hover:shadow-md"
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="font-semibold text-lg text-gray-800">{comp.provider.name}</h3>
                    {index === 0 && (
                      <Badge className="bg-green-500 hover:bg-green-600" data-testid="best-value-badge">
                        Best Value
                      </Badge>
                    )}
                  </div>
                  
                  {/* Quality Scores Section */}
                  <div className="flex flex-wrap gap-2 mb-3">
                    {comp.provider.leapfrog_grade && comp.provider.leapfrog_grade !== 'Not Rated' && (
                      <div className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-50 rounded-lg border border-gray-200">
                        <Shield className="w-4 h-4 text-gray-600" />
                        <span className="text-xs font-medium text-gray-600">Leapfrog Grade:</span>
                        <span className={`text-sm font-bold px-2 py-0.5 rounded text-white ${getGradeColor(comp.provider.leapfrog_grade)}`}>
                          {comp.provider.leapfrog_grade}
                        </span>
                      </div>
                    )}
                    
                    {comp.provider.cms_star_rating && (
                      <div className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-50 rounded-lg border border-gray-200">
                        <Award className="w-4 h-4 text-gray-600" />
                        <span className="text-xs font-medium text-gray-600">CMS Rating:</span>
                        {renderStars(comp.provider.cms_star_rating)}
                      </div>
                    )}
                    
                    {comp.provider.patient_safety_score && (
                      <div className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-50 rounded-lg border border-gray-200">
                        <span className="text-xs font-medium text-gray-600">Safety Score:</span>
                        <span className="text-sm font-semibold text-blue-600">{comp.provider.patient_safety_score}/100</span>
                      </div>
                    )}
                  </div>
                  
                  <div className="flex items-center gap-4 text-sm text-gray-600">
                    <span className="flex items-center gap-1">
                      <MapPin className="w-4 h-4" />
                      {comp.distance_miles} miles
                    </span>
                    {comp.provider.phone && (
                      <span className="flex items-center gap-1">
                        <Phone className="w-4 h-4" />
                        {comp.provider.phone}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-500 mt-1">
                    {comp.provider.address}, {comp.provider.city}, {comp.provider.state} {comp.provider.zip_code}
                  </p>
                </div>
              </div>

              {/* Main Cost Display */}
              <div className={`rounded-xl p-4 mt-4 border-2 ${
                comp.is_in_network 
                  ? 'bg-gradient-to-br from-green-50 to-emerald-50 border-green-200' 
                  : 'bg-gradient-to-br from-orange-50 to-red-50 border-orange-300'
              }`}>
                <div className="flex items-center justify-between mb-2">
                  <p className={`text-sm font-semibold ${comp.is_in_network ? 'text-green-800' : 'text-orange-800'}`}>
                    Your Out-of-Pocket Cost
                  </p>
                  {comp.is_in_network ? (
                    <Badge className="bg-green-500 hover:bg-green-600 text-xs">✓ In-Network</Badge>
                  ) : (
                    <Badge className="bg-orange-500 hover:bg-orange-600 text-xs">⚠ Out-of-Network</Badge>
                  )}
                </div>
                <p className={`text-3xl font-bold ${comp.is_in_network ? 'text-green-700' : 'text-orange-700'}`} data-testid={`provider-${index}-cost`}>
                  ${comp.estimated_patient_cost.toFixed(2)}
                </p>
                {!comp.is_in_network && (
                  <p className="text-xs text-orange-600 mt-1 flex items-center gap-1">
                    ⚠ Out-of-network: Higher costs apply (no negotiated rate)
                  </p>
                )}
                {comp.savings > 0 && (
                  <p className="text-sm text-green-600 mt-1 flex items-center gap-1">
                    <TrendingDown className="w-4 h-4" />
                    Save ${comp.savings.toFixed(2)} vs highest cost provider
                  </p>
                )}
              </div>

              {/* Insurance-Specific Cost Breakdown */}
              <div className="mt-4 p-4 bg-gray-50 rounded-xl">
                <p className="text-xs font-semibold text-gray-700 mb-3 flex items-center gap-2">
                  Your {insuranceCarrier || 'Insurance'} Breakdown
                  {comp.is_in_network ? (
                    <span className="text-green-600 text-xs">(In-Network Benefits)</span>
                  ) : (
                    <span className="text-orange-600 text-xs">(Out-of-Network - Higher Costs)</span>
                  )}
                </p>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">
                      {comp.is_in_network ? `Negotiated Rate (${insuranceCarrier || 'Your Plan'}):` : 'Provider Charges (No Discount):'}
                    </span>
                    <span className="font-semibold text-gray-800">${comp.negotiated_rate?.toFixed(2) || comp.procedure_cost.toFixed(2)}</span>
                  </div>
                  
                  {/* Deductible Explanation */}
                  {comp.deductible_applied > 0 && (
                    <>
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <span className="text-gray-600">
                            Your {comp.is_in_network ? 'Deductible' : 'Out-of-Network Deductible'} Payment:
                          </span>
                          <p className="text-xs text-gray-500 mt-0.5">
                            (Remaining deductible you must pay first)
                          </p>
                        </div>
                        <span className="font-semibold text-orange-600 ml-2">${comp.deductible_applied.toFixed(2)}</span>
                      </div>
                      <div className="border-t border-dashed border-gray-300 my-1"></div>
                    </>
                  )}
                  
                  {/* Coinsurance Explanation */}
                  {comp.coinsurance_applied > 0 && (
                    <>
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <span className="text-gray-600">
                            Your Coinsurance {comp.is_in_network ? '(20%)' : '(40% - Out-of-Network)'}:
                          </span>
                          <p className="text-xs text-gray-500 mt-0.5">
                            (Your share after deductible is met)
                          </p>
                        </div>
                        <span className="font-semibold text-orange-600 ml-2">${comp.coinsurance_applied.toFixed(2)}</span>
                      </div>
                      <div className="border-t border-dashed border-gray-300 my-1"></div>
                    </>
                  )}
                  
                  {/* Copay */}
                  {comp.copay_applied > 0 && (
                    <>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Copay:</span>
                        <span className="font-semibold text-orange-600">${comp.copay_applied.toFixed(2)}</span>
                      </div>
                      <div className="border-t border-dashed border-gray-300 my-1"></div>
                    </>
                  )}
                  
                  {/* Total Calculation */}
                  <div className="bg-blue-50 p-2 rounded mt-2 mb-2">
                    <div className="flex justify-between">
                      <span className="font-semibold text-gray-700">
                        {insuranceCarrier || 'Your Insurance'} Pays:
                      </span>
                      <span className="font-semibold text-blue-600">${comp.insurance_pays?.toFixed(2) || '0.00'}</span>
                    </div>
                  </div>
                  
                  {/* Clear Addition Showing Out-of-Pocket Calculation */}
                  <div className="bg-green-50 p-3 rounded border-2 border-green-300">
                    <div className="space-y-2">
                      <p className="font-bold text-gray-800 text-sm">Your Out-of-Pocket Cost Calculation:</p>
                      
                      {/* Show the calculation step by step */}
                      <div className="space-y-1 text-sm bg-white p-3 rounded">
                        <div className="text-xs text-gray-600 mb-2 font-semibold">
                          {comp.is_in_network 
                            ? 'In-Network Formula: Deductible + (20% of remaining amount) + Copay'
                            : 'Out-of-Network Formula: Deductible + (40% of remaining amount)'}
                        </div>
                        
                        {/* Deductible */}
                        {comp.deductible_applied > 0 ? (
                          <div className="flex justify-between text-gray-700">
                            <span>Your Deductible Payment:</span>
                            <span className="font-semibold">${comp.deductible_applied.toFixed(2)}</span>
                          </div>
                        ) : (
                          <div className="flex justify-between text-gray-700">
                            <span>Your Deductible Payment:</span>
                            <span className="font-semibold text-green-600">$0.00 (Met!)</span>
                          </div>
                        )}
                        
                        {/* Show what's subject to coinsurance */}
                        {comp.coinsurance_applied > 0 && (
                          <>
                            <div className="text-xs text-gray-500 pl-4 italic">
                              Remaining after deductible: ${(comp.negotiated_rate - comp.deductible_applied).toFixed(2)}
                            </div>
                            <div className="flex justify-between text-gray-700">
                              <span>+ Your {comp.is_in_network ? '20%' : '40%'} Coinsurance:</span>
                              <span className="font-semibold">${comp.coinsurance_applied.toFixed(2)}</span>
                            </div>
                            <div className="text-xs text-gray-500 pl-4 italic">
                              ({comp.is_in_network ? '20%' : '40%'} × ${(comp.negotiated_rate - comp.deductible_applied).toFixed(2)})
                            </div>
                          </>
                        )}
                        
                        {/* Copay */}
                        {comp.copay_applied > 0 && (
                          <div className="flex justify-between text-gray-700">
                            <span>+ Your Copay:</span>
                            <span className="font-semibold">${comp.copay_applied.toFixed(2)}</span>
                          </div>
                        )}
                        
                        {/* Equals line */}
                        <div className="border-t-2 border-green-400 pt-2 mt-2">
                          <div className="flex justify-between items-center">
                            <span className="font-bold text-gray-800">= Total You Pay:</span>
                            <span className={`text-2xl font-bold ${comp.is_in_network ? 'text-green-700' : 'text-orange-700'}`}>
                              ${comp.estimated_patient_cost.toFixed(2)}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Verification */}
                  <div className="text-xs text-gray-500 text-center mt-2 pt-2 border-t">
                    ✓ Total verified: ${(comp.insurance_pays + comp.estimated_patient_cost).toFixed(2)} = Provider charge
                  </div>
                </div>
                
                {/* Educational Notes */}
                {!comp.is_in_network && (
                  <div className="mt-3 p-2 bg-orange-100 rounded text-xs text-orange-800">
                    <strong>Out-of-Network:</strong> This provider doesn't have a contract with your insurance, 
                    so you'll pay the full deductible amount plus higher coinsurance (40-50% vs 20% in-network).
                  </div>
                )}
                
                {comp.deductible_applied > 0 && (
                  <div className="mt-2 p-2 bg-blue-50 rounded text-xs text-blue-800">
                    <strong>💡 About Your Deductible:</strong> You must pay ${comp.deductible_applied.toFixed(2)} toward 
                    your deductible before insurance starts covering their share. This is standard for elective procedures.
                  </div>
                )}
              </div>

              {/* Additional Quality Metrics */}
              {comp.provider.quality_metrics && (
                <div className="mt-4 pt-4 border-t">
                  <p className="text-xs font-semibold text-gray-700 mb-2">Quality Metrics</p>
                  <div className="grid grid-cols-3 gap-3">
                    {comp.provider.quality_metrics.patient_satisfaction && (
                      <div className="text-center p-2 bg-blue-50 rounded">
                        <p className="text-xs text-gray-600">Patient Satisfaction</p>
                        <p className="text-sm font-bold text-blue-600">{comp.provider.quality_metrics.patient_satisfaction}%</p>
                      </div>
                    )}
                    {comp.provider.quality_metrics.readmission_rate && (
                      <div className="text-center p-2 bg-purple-50 rounded">
                        <p className="text-xs text-gray-600">Readmission Rate</p>
                        <p className="text-sm font-bold text-purple-600">{comp.provider.quality_metrics.readmission_rate}%</p>
                      </div>
                    )}
                    {comp.provider.quality_metrics.infection_rate && (
                      <div className="text-center p-2 bg-green-50 rounded">
                        <p className="text-xs text-gray-600">Infection Rate</p>
                        <p className="text-sm font-bold text-green-600">{comp.provider.quality_metrics.infection_rate}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {comp.provider.specialties && comp.provider.specialties.length > 0 && (
                <div className="mt-3 flex gap-2 flex-wrap">
                  {comp.provider.specialties.map((specialty, idx) => (
                    <Badge key={idx} variant="secondary" className="text-xs">
                      {specialty}
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
    </div>
  );
}